import { useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import {
  Add as AddIcon,
  CalendarToday,
  Event,
  Description,
  FilterList,
  ViewModule,
  ViewList,
  ChevronLeft,
  ChevronRight,
  Today,
  Clear,
} from "@mui/icons-material";
import { useFormik } from "formik";
import * as Yup from "yup";
import api from "../services/api";
import ActionMenu from "../components/ui/ActionMenu";
import { useAuth } from "../context/AuthContext";
import { hasNavWrite } from "../utils/navPermissions";
import { toast } from "react-toastify";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Alert,
  MenuItem,
} from "@mui/material";
import Input from "../components/ui/Input";

const HOLIDAY_TYPE_OPTIONS = [
  { value: "public", label: "Public Holiday", color: "bg-green-600", textColor: "text-green-700", bgLight: "bg-green-100" },
  { value: "floating", label: "Floating Holiday", color: "bg-yellow-500", textColor: "text-yellow-800", bgLight: "bg-yellow-100" },
];

const validationSchema = Yup.object({
  name: Yup.string()
    .min(3, "Holiday name must be at least 3 characters")
    .required("Holiday name is required"),
  date: Yup.date().required("Date is required"),
  type: Yup.string()
    .oneOf(
      HOLIDAY_TYPE_OPTIONS.map((option) => option.value),
      "Invalid holiday type"
    )
    .required("Holiday type is required"),
  description: Yup.string(),
});

const DAYS_OF_WEEK = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

export default function Holidays() {
  const { user } = useAuth();
  const canManageHolidays = hasNavWrite(user, "/holidays");
  
  const [open, setOpen] = useState(false);
  const [holidays, setHolidays] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [holidayToDelete, setHolidayToDelete] = useState(null);
  
  // Calendar view state
  const [viewMode, setViewMode] = useState("list"); // "list" or "calendar"
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [filterStartDate, setFilterStartDate] = useState("");
  const [filterEndDate, setFilterEndDate] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  const formik = useFormik({
    initialValues: {
      name: "",
      date: "",
      type: "public",
      description: "",
    },
    validationSchema: validationSchema,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      setError("");
      setSuccess("");

      try {
        let normalizedDate = values.date;
        if (typeof normalizedDate === "string" && normalizedDate) {
          normalizedDate = normalizedDate.includes("T")
            ? normalizedDate
            : `${normalizedDate}T00:00:00.000Z`;
        }

        const payload = { ...values, date: normalizedDate };

        if (editingId) {
          await api.put(`/holidays/${editingId}`, payload);
          toast.success("Holiday updated successfully!");
        } else {
          await api.post("/holidays", payload);
          toast.success("Holiday added successfully!");
        }
        resetForm();
        setOpen(false);
        setEditingId(null);
        fetchHolidays();
      } catch (err) {
        const errorMsg = err.response?.data?.message || "Operation failed";
        setError(errorMsg);
        toast.error(errorMsg);
      } finally {
        setSubmitting(false);
      }
    },
  });

  const fetchHolidays = async () => {
    try {
      setLoading(true);
      const response = await api.get("/holidays");
      const data = Array.isArray(response.data) ? response.data : [];
      const normalized = data.map((holiday) => ({
        ...holiday,
        id: holiday._id || holiday.id,
      }));
      setHolidays(normalized);
    } catch (err) {
      console.error("Failed to fetch holidays", err);
      toast.error("Failed to fetch holidays");
    } finally {
      setLoading(false);
    }
  };

  const fetchLeaves = async () => {
    try {
      // Fetch leaves for the current user
      const response = await api.get("/leave/my-requests", {
        params: { status: "all" }
      });
      const data = Array.isArray(response.data) ? response.data : [];
      setLeaves(data);
    } catch (err) {
      console.error("Failed to fetch leaves", err);
      // Don't show error toast for leaves as it's optional
    }
  };

  useEffect(() => {
    fetchHolidays();
    fetchLeaves();
  }, []);

  const handleEdit = (holiday) => {
    const holidayId = holiday.id || holiday._id;
    setEditingId(holidayId);
    formik.setValues({
      name: holiday.name,
      date: holiday.date?.split("T")[0] || "",
      type: holiday.type || "public",
      description: holiday.description || "",
    });
    setOpen(true);
  };

  const handleDeleteClick = (holiday) => {
    setHolidayToDelete(holiday);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!holidayToDelete) return;
    try {
      const holidayId = holidayToDelete.id || holidayToDelete._id;
      await api.delete(`/holidays/${holidayId}`);
      toast.success("Holiday deleted successfully!");
      setDeleteDialogOpen(false);
      setHolidayToDelete(null);
      fetchHolidays();
    } catch (err) {
      toast.error("Failed to delete holiday");
      setDeleteDialogOpen(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setEditingId(null);
    formik.resetForm();
  };

  const getTypeColor = (type) => {
    const option = HOLIDAY_TYPE_OPTIONS.find((opt) => opt.value === type);
    return option || { color: "bg-gray-500", textColor: "text-gray-700", bgLight: "bg-gray-50" };
  };

  const formatDate = (dateString) => {
    if (!dateString) return "--";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      weekday: "short",
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Filter holidays by date range
  const filteredHolidays = useMemo(() => {
    let filtered = [...holidays];
    
    if (filterStartDate) {
      const start = new Date(filterStartDate);
      start.setHours(0, 0, 0, 0);
      filtered = filtered.filter(holiday => {
        const holidayDate = new Date(holiday.date);
        holidayDate.setHours(0, 0, 0, 0);
        return holidayDate >= start;
      });
    }
    
    if (filterEndDate) {
      const end = new Date(filterEndDate);
      end.setHours(23, 59, 59, 999);
      filtered = filtered.filter(holiday => {
        const holidayDate = new Date(holiday.date);
        holidayDate.setHours(0, 0, 0, 0);
        return holidayDate <= end;
      });
    }
    
    return filtered.sort((a, b) => new Date(a.date) - new Date(b.date));
  }, [holidays, filterStartDate, filterEndDate]);

  // Calendar grid generation
  const calendarDays = useMemo(() => {
    const firstDayOfMonth = new Date(currentYear, currentMonth, 1);
    const lastDayOfMonth = new Date(currentYear, currentMonth + 1, 0);
    const startDate = new Date(firstDayOfMonth);
    startDate.setDate(startDate.getDate() - startDate.getDay()); // Start from Sunday
    
    const days = [];
    const currentDate = new Date(startDate);
    
    // Generate 42 days (6 weeks)
    for (let i = 0; i < 42; i++) {
      const dateStr = currentDate.toISOString().split("T")[0];
      const dateObj = new Date(currentDate);
      dateObj.setHours(0, 0, 0, 0);
      
      // Find holidays for this date
      const dayHolidays = filteredHolidays.filter(h => {
        const holidayDate = new Date(h.date).toISOString().split("T")[0];
        return holidayDate === dateStr;
      });
      
      // Find leaves that include this date
      const dayLeaves = leaves.filter(leave => {
        if (!leave.from_date || !leave.to_date) return false;
        const fromDate = new Date(leave.from_date);
        const toDate = new Date(leave.to_date);
        fromDate.setHours(0, 0, 0, 0);
        toDate.setHours(0, 0, 0, 0);
        return dateObj >= fromDate && dateObj <= toDate;
      });
      
      days.push({
        date: new Date(currentDate),
        dateStr,
        isCurrentMonth: currentDate.getMonth() === currentMonth,
        isToday: dateStr === new Date().toISOString().split("T")[0],
        holidays: dayHolidays,
        leaves: dayLeaves,
      });
      
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    return days;
  }, [currentMonth, currentYear, filteredHolidays, leaves]);

  const navigateMonth = (direction) => {
    if (direction === "prev") {
      if (currentMonth === 0) {
        setCurrentMonth(11);
        setCurrentYear(currentYear - 1);
      } else {
        setCurrentMonth(currentMonth - 1);
      }
    } else {
      if (currentMonth === 11) {
        setCurrentMonth(0);
        setCurrentYear(currentYear + 1);
      } else {
        setCurrentMonth(currentMonth + 1);
      }
    }
  };

  const goToToday = () => {
    const today = new Date();
    setCurrentMonth(today.getMonth());
    setCurrentYear(today.getFullYear());
  };

  const clearFilters = () => {
    setFilterStartDate("");
    setFilterEndDate("");
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05,
      },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="mb-8"
      >
        <div className="flex justify-between items-center flex-wrap gap-4 mb-4">
          <div>
            <h1 className="text-4xl font-bold text-primary mb-2">
              Holiday Calendar
            </h1>
            <p className="text-gray-600">View and manage your organization's holidays</p>
          </div>
          <div className="flex items-center gap-3">
            {canManageHolidays && (
              <motion.button
                onClick={() => {
                  setEditingId(null);
                  formik.resetForm();
                  setOpen(true);
                }}
                className="btn-primary flex items-center gap-2 px-6 py-3"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <AddIcon />
                <span>Add Holiday</span>
              </motion.button>
            )}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`btn-secondary flex items-center gap-2 px-4 py-3 ${showFilters ? 'bg-primary text-white' : ''}`}
            >
              <FilterList />
              <span className="hidden sm:inline">Filters</span>
            </button>
            <div className="flex items-center gap-2 border border-gray-300 rounded-xl overflow-hidden">
              <button
                onClick={() => setViewMode("calendar")}
                className={`px-4 py-2 ${viewMode === "calendar" ? "bg-primary text-white" : "bg-white text-gray-700 hover:bg-gray-50"}`}
              >
                <ViewModule />
              </button>
              <button
                onClick={() => setViewMode("list")}
                className={`px-4 py-2 ${viewMode === "list" ? "bg-primary text-white" : "bg-white text-gray-700 hover:bg-gray-50"}`}
              >
                <ViewList />
              </button>
            </div>
          </div>
        </div>

        {/* Date Range Filters */}
        {showFilters && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="card mb-6 p-4"
          >
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex-1 min-w-[200px]">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Start Date
                </label>
                <Input
                  type="date"
                  value={filterStartDate}
                  onChange={(e) => setFilterStartDate(e.target.value)}
                  className="w-full"
                />
              </div>
              <div className="flex-1 min-w-[200px]">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  End Date
                </label>
                <Input
                  type="date"
                  value={filterEndDate}
                  onChange={(e) => setFilterEndDate(e.target.value)}
                  className="w-full"
                />
              </div>
              <div className="flex items-end gap-2">
                <button
                  onClick={clearFilters}
                  className="btn-secondary flex items-center gap-2 px-4 py-2"
                >
                  <Clear />
                  <span>Clear</span>
                </button>
              </div>
            </div>
            {(filterStartDate || filterEndDate) && (
              <p className="text-sm text-gray-500 mt-3">
                Showing {filteredHolidays.length} holiday{filteredHolidays.length !== 1 ? "s" : ""}
                {filterStartDate && ` from ${new Date(filterStartDate).toLocaleDateString()}`}
                {filterEndDate && ` to ${new Date(filterEndDate).toLocaleDateString()}`}
              </p>
            )}
          </motion.div>
        )}
      </motion.div>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 3 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 3, borderRadius: 3 }}>
          {success}
        </Alert>
      )}

      {/* Calendar View */}
      {viewMode === "calendar" && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="card p-6"
        >
          {/* Calendar Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigateMonth("prev")}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <ChevronLeft />
              </button>
              <h2 className="text-2xl font-bold text-primary min-w-[200px] text-center">
                {MONTHS[currentMonth]} {currentYear}
              </h2>
              <button
                onClick={() => navigateMonth("next")}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <ChevronRight />
              </button>
            </div>
            <button
              onClick={goToToday}
              className="btn-secondary flex items-center gap-2 px-4 py-2"
            >
              <Today />
              <span>Today</span>
            </button>
          </div>

          {/* Calendar Grid */}
          {loading ? (
            <div className="grid grid-cols-7 gap-2">
              {Array.from({ length: 42 }).map((_, i) => (
                <div key={i} className="aspect-square skeleton rounded-lg"></div>
              ))}
            </div>
          ) : (
            <>
              {/* Day Headers */}
              <div className="grid grid-cols-7 gap-2 mb-2">
                {DAYS_OF_WEEK.map((day) => (
                  <div
                    key={day}
                    className="text-center font-semibold text-gray-600 py-2"
                  >
                    {day}
                  </div>
                ))}
              </div>

              {/* Calendar Days */}
              <div className="grid grid-cols-7 gap-1">
                {calendarDays.map((day, index) => {
                  const hasHolidays = day.holidays.length > 0;
                  const hasLeaves = day.leaves && day.leaves.length > 0;
                  const totalItems = day.holidays.length + (day.leaves?.length || 0);
                  
                  return (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: index * 0.01 }}
                      className={`
                        min-h-[100px] border rounded-lg p-1.5 overflow-hidden
                        ${day.isCurrentMonth ? "border-gray-200 bg-white" : "border-gray-100 bg-gray-50 opacity-50"}
                        ${day.isToday ? "border-primary border-2 bg-primary/5" : ""}
                        ${totalItems > 0 ? "cursor-pointer hover:shadow-md transition-shadow" : "hover:bg-gray-50"}
                      `}
                    >
                      <div className="flex flex-col h-full">
                        <div
                          className={`text-xs font-semibold mb-1 ${
                            day.isToday
                              ? "text-primary font-bold"
                              : day.isCurrentMonth
                              ? "text-gray-900"
                              : "text-gray-400"
                          }`}
                        >
                          {day.date.getDate()}
                        </div>
                        <div className="flex-1 overflow-y-auto space-y-0.5">
                          {/* Show holidays first */}
                          {day.holidays.slice(0, 2).map((holiday, idx) => {
                            const colors = getTypeColor(holiday.type);
                            return (
                              <div
                                key={`holiday-${holiday.id}`}
                                className={`
                                  text-[10px] px-1.5 py-0.5 rounded
                                  ${colors.color} text-white
                                  truncate font-medium
                                `}
                                title={holiday.name}
                              >
                                {holiday.name}
                              </div>
                            );
                          })}
                          {/* Show leaves */}
                          {day.leaves && day.leaves.slice(0, hasHolidays ? 1 : 2).map((leave, idx) => {
                            const statusColors = {
                              approved: "bg-green-500",
                              pending: "bg-yellow-500",
                              rejected: "bg-red-500",
                            };
                            const statusColor = statusColors[leave.status] || "bg-gray-500";
                            const leaveType = leave.leave_type || "Leave";
                            return (
                              <div
                                key={`leave-${leave.id || leave._id}`}
                                className={`
                                  text-[10px] px-1.5 py-0.5 rounded
                                  ${statusColor} text-white
                                  truncate font-medium
                                `}
                                title={`${leaveType} - ${leave.status}`}
                              >
                                {leaveType}
                              </div>
                            );
                          })}
                          {totalItems > 3 && (
                            <div className="text-[10px] text-gray-500 font-semibold">
                              +{totalItems - 3} more
                            </div>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </>
          )}
        </motion.div>
      )}

      {/* List View */}
      {viewMode === "list" && (
        <div className="card overflow-visible hover:!translate-y-0 hover:!shadow-soft">
          {loading ? (
            <div className="divide-y divide-gray-100">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="flex items-center gap-4 p-4">
                  <div className="w-10 h-10 skeleton rounded-xl flex-shrink-0"></div>
                  <div className="flex-1 space-y-2">
                    <div className="h-4 skeleton rounded w-1/3"></div>
                    <div className="h-3 skeleton rounded w-1/4"></div>
                  </div>
                  <div className="w-24 h-6 skeleton rounded-full"></div>
                </div>
              ))}
            </div>
          ) : filteredHolidays.length === 0 ? (
            <div className="text-center py-16">
              <CalendarToday sx={{ fontSize: 56, color: "#d1d5db", mb: 2 }} />
              <p className="text-gray-500 text-lg font-medium">No holidays found</p>
              <p className="text-gray-400 text-sm mt-1">
                {filterStartDate || filterEndDate
                  ? "Try adjusting your date filters"
                  : "No holidays have been added yet"}
              </p>
              {(filterStartDate || filterEndDate) && (
                <button
                  onClick={clearFilters}
                  className="mt-4 text-primary hover:underline text-sm font-medium"
                >
                  Clear filters
                </button>
              )}
            </div>
          ) : (
            <>
              {/* Table header */}
              <div className="hidden sm:grid grid-cols-12 gap-4 px-5 py-3 bg-gray-50 border-b border-gray-200 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                <div className="col-span-4">Holiday</div>
                <div className="col-span-3">Date</div>
                <div className="col-span-2">Type</div>
                <div className="col-span-2">Weekend</div>
                <div className="col-span-1 text-right">
                  {canManageHolidays && "Actions"}
                </div>
              </div>
              <motion.div
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                className="divide-y divide-gray-100"
              >
                {filteredHolidays.map((holiday) => {
                  const typeColors = getTypeColor(holiday.type);
                  const typeLabel =
                    HOLIDAY_TYPE_OPTIONS.find((opt) => opt.value === holiday.type)
                      ?.label || holiday.type;
                  return (
                    <motion.div
                      key={holiday.id}
                      variants={cardVariants}
                      className="grid grid-cols-12 gap-4 items-center px-5 py-4 hover:bg-gray-50 transition-colors"
                    >
                      {/* Name + description */}
                      <div className="col-span-12 sm:col-span-4 flex items-center gap-3 min-w-0">
                        <div className="w-9 h-9 flex-shrink-0 bg-primary/10 rounded-xl flex items-center justify-center text-primary">
                          <Event sx={{ fontSize: 18 }} />
                        </div>
                        <div className="min-w-0">
                          <p className="font-semibold text-gray-900 truncate">
                            {holiday.name}
                          </p>
                          {holiday.description && (
                            <p className="text-xs text-gray-400 truncate mt-0.5">
                              {holiday.description}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Date */}
                      <div className="col-span-6 sm:col-span-3 flex items-center gap-1.5 text-sm text-gray-600">
                        <CalendarToday sx={{ fontSize: 14, color: "#9ca3af" }} />
                        <span>{formatDate(holiday.date)}</span>
                      </div>

                      {/* Type badge */}
                      <div className="col-span-3 sm:col-span-2">
                        <span
                          className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${typeColors.color} text-white`}
                        >
                          {typeLabel}
                        </span>
                      </div>

                      {/* Weekend badge — auto-computed from date */}
                      <div className="col-span-2 sm:col-span-2">
                        {(() => {
                          const day = holiday.date ? new Date(holiday.date).getDay() : -1;
                          const isWeekend = day === 0 || day === 6;
                          return isWeekend ? (
                            <span className="inline-block px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700">
                              Yes
                            </span>
                          ) : (
                            <span className="text-xs text-gray-400">No</span>
                          );
                        })()}
                      </div>

                      {/* Actions */}
                      <div className="col-span-1 sm:col-span-1 flex justify-end">
                        {canManageHolidays && (
                          <ActionMenu
                            onEdit={handleEdit}
                            onDelete={handleDeleteClick}
                            item={holiday}
                          />
                        )}
                      </div>
                    </motion.div>
                  );
                })}
              </motion.div>
              <div className="px-5 py-3 bg-gray-50 border-t border-gray-100 text-xs text-gray-400">
                {filteredHolidays.length} holiday{filteredHolidays.length !== 1 ? "s" : ""}
                {(filterStartDate || filterEndDate) && " (filtered)"}
              </div>
            </>
          )}
        </div>
      )}

      {/* Add/Edit Dialog — overflow:visible so MUI floating labels are not clipped */}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { overflow: "visible" } }}
      >
        <form onSubmit={formik.handleSubmit}>
          <DialogTitle sx={{ fontSize: "1.5rem", fontWeight: 600 }}>
            {editingId ? "Edit Holiday" : "Add Holiday"}
          </DialogTitle>
          <DialogContent sx={{ pt: 3, overflow: "visible" }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  id="name"
                  name="name"
                  label="Holiday Name"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.name && Boolean(formik.errors.name)}
                  helperText={formik.touched.name && formik.errors.name}
                  sx={{
                    mt: 0.5,
                    "& .MuiOutlinedInput-root": { borderRadius: 3 },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  id="date"
                  name="date"
                  label="Date"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={formik.values.date}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.date && Boolean(formik.errors.date)}
                  helperText={formik.touched.date && formik.errors.date}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3 } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  id="type"
                  name="type"
                  label="Holiday Type"
                  value={formik.values.type}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.type && Boolean(formik.errors.type)}
                  helperText={formik.touched.type && formik.errors.type}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3 } }}
                >
                  {HOLIDAY_TYPE_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  id="description"
                  name="description"
                  label="Description (Optional)"
                  value={formik.values.description}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3 } }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 2 }}>
            <Button onClick={handleClose} sx={{ borderRadius: 2 }}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={formik.isSubmitting}
              sx={{ borderRadius: 2 }}
            >
              {formik.isSubmitting ? "Saving..." : "Save"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <p>
            Are you sure you want to delete "{holidayToDelete?.name}"? This
            action cannot be undone.
          </p>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
